import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, MessageSquareText } from "lucide-react";
import api from "@/lib/api";
import { Empty, TabHeading, fmtDate } from "./shared";

const ROWS = [
  { key: "strengths", label: "Strengths" },
  { key: "improvements", label: "Areas to Improve" },
  { key: "homework", label: "Homework" },
  { key: "next_focus", label: "Next Focus" },
];

export default function FeedbackTab({ athleteId, refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/feedback?athlete_id=${athleteId}`);
      setItems(data);
    } catch { toast.error("Failed to load feedback."); } finally { setLoading(false); }
  }, [athleteId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const del = async (id) => {
    try { await api.delete(`/feedback/${id}`); toast.success("Feedback deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  if (loading) return <div className="flex justify-center py-16 text-white/40"><Loader2 className="animate-spin" /></div>;

  return (
    <div data-testid="af-feedback">
      <TabHeading>Coach Feedback</TabHeading>
      {items.length === 0 ? (
        <Empty testid="af-feedback-empty">No feedback logged yet. Add session feedback from Quick Actions.</Empty>
      ) : (
        <div className="space-y-4" data-testid="af-feedback-list">
          {items.map((f) => (
            <div key={f.id} data-testid={`af-feedback-${f.id}`} className="bg-[#121212] border border-white/10 p-5">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex items-center gap-2">
                  <MessageSquareText size={16} className="text-white/50" />
                  <div>
                    <p className="font-display font-bold uppercase tracking-tight">{f.focus || "Session"}</p>
                    <p className="text-white/40 text-xs">{fmtDate(f.session_date || f.created_at)}{f.author_name ? ` · ${f.author_name}` : ""}</p>
                  </div>
                </div>
                <button data-testid={`af-feedback-del-${f.id}`} onClick={() => del(f.id)} className="text-white/30 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {ROWS.map((r) => f[r.key] && (
                  <div key={r.key}>
                    <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1">{r.label}</p>
                    <p className="text-white/75 text-sm">{f[r.key]}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
